import { useCallback, useState } from 'react';
import { useNavigate, useOutletContext } from 'react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  BRIEFS_QUERY_KEYS,
  briefsQueryOptions,
  type Brief,
  type BriefStatus,
} from '@/entities/brief';
import { useBriefAgentReview } from '@/features/brief-agent-review';
import {
  BriefGenerationProgressModal,
  BriefIntakeModal,
  type BriefIntakeValues,
} from '@/features/brief-generation';
import { notificationService } from '@/lib/services/notification.service';
import { LeavesTogether, SETTLED_EXIT_SECONDS } from '@/shared/ui/animations';
import { useDelayedFlag } from '@/shared/hooks';
import { ROUTES } from '@/shared/config/routes';
import { SEARCH_DEBOUNCE_MS } from '@/shared/constants/search.constants';
import { useDebounce } from '@/shared/hooks';
import { useModalState } from '@/shared/hooks/useModalState';
import type { ProjectDetailOutletContext } from '../../projectDetail.types';
import { resolveBriefSort } from './briefs.constants';
import { BriefsPagination } from './components/BriefsPagination';
import { BriefsTable } from './components/BriefsTable';
import { BriefsToolbar } from './components/BriefsToolbar';
import { useTranslation } from 'react-i18next';

/**
 * Default page size for the briefs list (matches Folders' `FOLDERS_PAGE_SIZE`).
 */
const BRIEFS_PAGE_SIZE = 20;

/**
 * Atomic state object for the briefs list view.
 *
 * `search`, `sortId`, `status`, and `page` MUST live in the same `useState`
 * object so the search / sort / filter handlers can reset `page` to 1 in the
 * SAME setState call. This eliminates the need for a
 * `useEffect(() => setPage(1), [search])` watcher (banned by the project-wide
 * `react/no-use-effect` rule).
 */
interface BriefsParamsState {
  search: string;
  page: number;
  pageSize: number;
  /** Active `SortChipPopover` choice id, or `null` for the endpoint default order. */
  sortId: string | null;
  /** Active status filter, or `null` for "All statuses". */
  status: BriefStatus | null;
}

/**
 * Briefs list view.
 *
 * Page-level orchestrator wiring:
 *   - {@link briefsQueryOptions} composed with `useQuery` for server-side
 *     data (search + pagination via the SDK).
 *   - {@link useDebounce} on the controlled search value (`SEARCH_DEBOUNCE_MS`).
 *   - {@link BriefsToolbar} for the search input and "New brief" CTA.
 *   - {@link BriefsTable} for the rows (loading / error / empty / data).
 *   - {@link BriefsPagination} for prev/next controls (defensively returns
 *     `null` when `totalPages <= 1`).
 *
 * The section names itself nowhere: the shell renders the one project header,
 * and the sidebar says which section you are in. `total` still drives
 * `totalPages` for pagination but is not surfaced inline.
 *
 * **No `useEffect`** — `useDebounce` consumes one internally (allowed; the
 * rule only forbids NEW `useEffect` in our code). Page-reset on search is
 * handled inside the `handleSearchChange` setter (atomic).
 */
export function Briefing() {
  const { t } = useTranslation('projects');

  const { project } = useOutletContext<ProjectDetailOutletContext>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  // Agent brief review (POC) — fire-and-forget review of freshly created briefs.
  const { review } = useBriefAgentReview();

  // Brief generation modal flow. Each modal gets a fresh instance per open so the
  // feature's internal hooks (`useBriefIntakeForm`, `useBriefGeneration`) reset
  // state through unmount — they are explicitly designed NOT to reset via prop
  // change. See `BriefIntakeModal` JSDoc and the design doc §6.
  const intakeModal = useModalState();
  const progressModal = useModalState();
  // Payload threaded from intake → progress. Stored in local state because
  // the two modals are siblings; intake unmounts on submit, so its form
  // payload would be lost otherwise.
  const [generationPayload, setGenerationPayload] = useState<BriefIntakeValues | null>(null);

  const [params, setParams] = useState<BriefsParamsState>({
    search: '',
    page: 1,
    pageSize: BRIEFS_PAGE_SIZE,
    sortId: null,
    status: null,
  });

  const debouncedSearch = useDebounce(params.search, SEARCH_DEBOUNCE_MS);

  // Resolve the chip's sort id to the endpoint's sortBy/sortOrder tuple; `null`
  // (no chip / cleared) omits both so the endpoint applies its default order.
  const sort = resolveBriefSort(params.sortId);

  const briefsQuery = useQuery(
    briefsQueryOptions({
      projectId: project.platformProjectId,
      page: params.page,
      pageSize: params.pageSize,
      search: debouncedSearch || undefined,
      status: params.status ? [params.status] : undefined,
      sortBy: sort?.sortBy,
      sortOrder: sort?.sortOrder,
    }),
  );

  /**
   * Atomic search update: resets `page` to 1 in the SAME setState as the new
   * search value. This is the no-`useEffect` page-reset pattern.
   */
  const handleSearchChange = useCallback((value: string) => {
    setParams((prev) => ({ ...prev, search: value, page: 1 }));
  }, []);

  const handlePageChange = useCallback((page: number) => {
    setParams((prev) => ({ ...prev, page }));
  }, []);

  // Sort + filter both reset to page 1 atomically (same no-`useEffect` pattern
  // as search) so a narrowed result set never lands the user on a now-empty page.
  const handleSortChange = useCallback((sortId: string | null) => {
    setParams((prev) => ({ ...prev, sortId, page: 1 }));
  }, []);

  const handleStatusChange = useCallback((status: BriefStatus | null) => {
    setParams((prev) => ({ ...prev, status, page: 1 }));
  }, []);

  // Clears search + status (and resets to page 1) from the no-results state.
  const handleClearFilters = useCallback(() => {
    setParams((prev) => ({ ...prev, search: '', status: null, page: 1 }));
  }, []);

  const handleNewBriefClick = useCallback(() => {
    intakeModal.open();
  }, [intakeModal]);

  /**
   * Intake → Progress hand-off. Stores the payload locally (so the progress
   * modal can read it as a prop), closes intake, opens progress.
   */
  const handleGenerate = useCallback(
    (payload: BriefIntakeValues) => {
      setGenerationPayload(payload);
      intakeModal.close();
      progressModal.open();
    },
    [intakeModal, progressModal],
  );

  /**
   * Progress success → invalidate the project's broad briefs `lists` key
   * (covers any pagination/search/sort variant), close the progress modal,
   * drop the cached payload, and navigate to the new brief's detail page.
   * The progress modal does NOT auto-close on success per design — wiring
   * owns that decision.
   */
  const handleGenerationSuccess = useCallback(
    (brief: Brief) => {
      // Agent brief review (POC) — fire-and-forget on creation.
      void review(brief, project.platformProjectId);
      void queryClient.invalidateQueries({
        queryKey: BRIEFS_QUERY_KEYS.lists(project.platformProjectId),
      });
      progressModal.close();
      setGenerationPayload(null);
      void navigate(ROUTES.PROJECT_BRIEF_DETAIL(project.platformProjectId, brief.id));
      // The success toast lands on the brief editor, anchored bottom-right over
      // the comments panel — give it an explicit Dismiss CTA so the user can
      // clear it immediately (it also auto-hides on the normal timer).
      const toastKey = notificationService.showSuccess(t('projectDetail.briefing.briefGenerated'), {
        action: {
          label: t('projectDetail.briefing.dismiss'),
          onClick: () => notificationService.close(toastKey),
        },
      });
    },
    [navigate, progressModal, project.platformProjectId, queryClient, review, t],
  );

  const handleRowClick = useCallback(
    (brief: Brief) => {
      void navigate(ROUTES.PROJECT_BRIEF_DETAIL(project.platformProjectId, brief.id));
    },
    [navigate, project.platformProjectId],
  );

  // The full `briefsQuery` result is not referentially stable across renders,
  // so destructure `refetch` (which IS stable) before passing it to useCallback
  // (per @tanstack/query/no-unstable-deps).
  const { refetch: refetchBriefs } = briefsQuery;
  const handleRetry = useCallback(() => {
    void refetchBriefs();
  }, [refetchBriefs]);

  const items = briefsQuery.data?.items ?? [];
  const total = briefsQuery.data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / params.pageSize));

  /**
   * Drives the empty-state copy variant: when a search term (flushed through
   * the debounce) OR a status filter is active, an empty result set should read
   * "No matching briefs" instead of the initial-empty "No briefs yet" copy (and
   * suppress the create CTA). Sourced from `debouncedSearch`, NOT `params.search`,
   * so the variant flips at the same moment the query refetches.
   */
  const hasActiveFilters = Boolean(debouncedSearch) || params.status !== null;

  const hasRows = items.length > 0;

  /**
   * Show the toolbar when there is something to search or filter, while the
   * first load resolves, or when a filter is already on so a zero-match result
   * can still be cleared. Hidden for the true-empty and error states, where
   * the controls would act on nothing.
   */
  // Nothing announces loading until it has lasted long enough to be worth
  // announcing; a fast answer replaces the previous content directly.
  const showLoadingIndicator = useDelayedFlag(briefsQuery.isLoading && !hasRows);
  // Gated on the indicator, not on raw loading: the toolbar and the skeletons
  // are one picture and should arrive together, so a fast answer shows neither.
  const showToolbar = hasRows || showLoadingIndicator || hasActiveFilters;

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4">
      {/* Only the toolbar has a staggered entrance. The table below settles
          into one of several states, so it crossfades once on its own rather
          than riding the cascade. */}
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-lg bg-card">
        <LeavesTogether present={showToolbar} exitSeconds={SETTLED_EXIT_SECONDS}>
          <BriefsToolbar
            pending={showLoadingIndicator}
            searchValue={params.search}
            onSearchChange={handleSearchChange}
            sortId={params.sortId}
            onSortChange={handleSortChange}
            statusFilter={params.status}
            onStatusChange={handleStatusChange}
            onNewBriefClick={handleNewBriefClick}
          />
        </LeavesTogether>

        <div className="flex min-h-0 flex-1 flex-col overflow-auto px-section pb-section">
          {/* No entrance wrapper here: the table owns one, keyed on whichever
              state it settles into, so the slot does not fade in ahead of
              content that is about to be replaced. */}
          <div className="flex min-h-0 flex-1 flex-col">
            <BriefsTable
              briefs={items}
              isLoading={briefsQuery.isLoading}
              showLoadingIndicator={showLoadingIndicator}
              isError={briefsQuery.isError}
              isPlaceholderData={briefsQuery.isPlaceholderData}
              hasActiveFilters={hasActiveFilters}
              onRetry={handleRetry}
              onRowClick={handleRowClick}
              onNewBrief={handleNewBriefClick}
              onClearFilters={handleClearFilters}
            />
          </div>
        </div>

        {totalPages > 1 && (
          <div className="flex justify-center pb-section">
            <BriefsPagination
              currentPage={params.page}
              totalPages={totalPages}
              onPageChange={handlePageChange}
            />
          </div>
        )}
      </div>

      {/*
        Fresh instance per open: each modal's internal hook state (form fields,
        abort controllers, captured payload) resets by mounting a new instance,
        since the feature's hooks do NOT implement prop-driven resets.

        `key` gives that reset; `isMounted` is what keeps the node in the tree
        for the exit animation. Guarding on `isOpen` instead removes the node in
        the same tick as the state flip, so Radix never applies
        `data-state="closed"` and the exit never renders at all.

        The keys are prefixed because the two modals are siblings and their
        instance counters advance independently — a bare counter collides on the
        handoff, where intake is still exiting as progress mounts.
      */}
      {intakeModal.isMounted && (
        <BriefIntakeModal
          key={`intake-${intakeModal.instanceKey}`}
          open={intakeModal.isOpen}
          onClose={intakeModal.close}
          onGenerate={handleGenerate}
        />
      )}
      {progressModal.isMounted && (
        <BriefGenerationProgressModal
          key={`progress-${progressModal.instanceKey}`}
          open={progressModal.isOpen}
          onClose={progressModal.close}
          projectId={project.platformProjectId}
          payload={generationPayload}
          onSuccess={handleGenerationSuccess}
        />
      )}
    </div>
  );
}
